import React, { useState, useMemo } from "react";
import { FORMULAS, RM_POINTS, weightForReps, roundTo, fmt, toNum } from "../lib/calc";

export const FormulaComparator = ({ unit, rounding }) => {
  const [weight, setWeight] = useState(245);
  const [reps, setReps] = useState(5);

  const rows = useMemo(() => {
    return Object.keys(FORMULAS).map((k) => {
      const est = FORMULAS[k].fn(toNum(weight), toNum(reps));
      return { key: k, label: FORMULAS[k].label, eq: FORMULAS[k].eq, oneRM: est };
    });
  }, [weight, reps]);

  const avg = rows.find((r) => r.key === "average").oneRM;

  const projections = useMemo(() => {
    return RM_POINTS.map((r) => ({
      reps: r,
      load: roundTo(weightForReps(avg, r), rounding),
    }));
  }, [avg, rounding]);

  return (
    <div className="tactical-card p-5 sm:p-6 animate-fade" data-testid="formula-comparator">
      <div className="mb-5">
        <div className="eyebrow">Estimateur avancé</div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase text-white leading-none mt-1">
          Epley vs Brzycki · Projection RM
        </h2>
        <p className="text-xs text-slate-400 mt-2 max-w-2xl">
          À partir d&apos;un set réel (ex. 5RM), estime le 1RM avec les deux
          formules majeures et projette toutes les autres RM (2, 3, 5, 8, 10,
          12, 15, 20…) via la moyenne des deux formules.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="eyebrow text-[10px]">Poids soulevé</span>
              <div className="flex items-stretch">
                <input
                  data-testid="fc-input-weight"
                  type="number"
                  min="0"
                  step="0.5"
                  value={weight}
                  onChange={(e) =>
                    setWeight(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  className="w-full bg-[#12141a] border border-white/10 rounded-l-sm px-3 py-3 text-xl font-mono font-bold text-white focus:border-[#cd383d] outline-none tabular"
                />
                <span className="flex items-center px-3 bg-[#1f232d] border border-l-0 border-white/10 rounded-r-sm text-xs font-mono text-slate-400 uppercase">
                  {unit}
                </span>
              </div>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="eyebrow text-[10px]">Répétitions (RM)</span>
              <input
                data-testid="fc-input-reps"
                type="number"
                min="1"
                max="30"
                step="1"
                value={reps}
                onChange={(e) =>
                  setReps(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full bg-[#12141a] border border-white/10 rounded-sm px-3 py-3 text-xl font-mono font-bold text-white focus:border-[#cd383d] outline-none tabular"
              />
            </label>
          </div>

          <div className="flex flex-col gap-2 mt-2">
            {rows.map((r) => (
              <div
                key={r.key}
                data-testid={`fc-row-${r.key}`}
                className={`flex items-center justify-between p-3 rounded-sm border ${
                  r.key === "average"
                    ? "border-[#cd383d]/60 bg-[#cd383d]/[0.06]"
                    : "border-white/10 bg-black/20"
                }`}
              >
                <div>
                  <div
                    className={`font-display text-lg font-extrabold uppercase ${
                      r.key === "average" ? "text-[#ff2e2e]" : "text-white"
                    }`}
                  >
                    {r.label}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    {r.eq}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-2xl font-black text-white tabular">
                    {fmt(roundTo(r.oneRM, rounding), rounding ? 0 : 1)}
                  </div>
                  <div className="text-[10px] font-mono uppercase text-slate-500">
                    1RM {unit}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="eyebrow text-[10px] mb-2">
            Projection toutes les RM (moyenne des formules)
          </div>
          <div className="grid grid-cols-2 gap-2">
            {projections.map((p) => (
              <div
                key={p.reps}
                data-testid={`fc-rm-${p.reps}`}
                className="flex items-baseline justify-between p-2.5 rounded-sm border border-white/10 bg-black/20"
              >
                <span className="font-mono text-xs uppercase tracking-widest text-slate-500">
                  {p.reps}RM
                </span>
                <span className="font-mono font-bold text-white tabular">
                  {fmt(p.load, rounding ? 0 : 1)}{" "}
                  <span className="text-[10px] text-slate-500 uppercase">
                    {unit}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
